import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Switch } from "@progress/kendo-react-inputs";
import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import LoadingSpinner from "../../../Shared/Components/LoadingSpinner";
import { SvgIcon } from "@progress/kendo-react-common";
import { infoCircleIcon, graphIcon, xIcon } from "@progress/kendo-svg-icons";
import { useLocalization } from "@progress/kendo-react-intl";
import { enMessages } from "../../../../messages/en-US";
import {
  PDFViewer,
  PDFViewerRef,
  ZoomMode,
  LockModeType,
} from "@embedpdf/react-pdf-viewer";
import SelectAllMultiSelectTree from "../../../Shared/Components/SelectAllMultiSelectTree";
import PeriodYearTree, { recentPeriods } from "./PeriodYearTree";
import { MachineReadDto } from "../../../../Models/Machine/MachinesReadDto";
import useIdentityStore from "../../../../Zustand/identityStore";
import { StandardReportRequest } from "./ReportsPdfBuilderTypes";

interface Props {
  machines: MachineReadDto[];
  onGenerate: (
    request: StandardReportRequest,
    signal?: AbortSignal
  ) => Promise<ArrayBuffer>;
  filtersOpen: boolean;
  onCloseFilters: () => void;
}

type PeriodMode = "months" | "weeks";

const toOptions = (ids: string[]) => ids.map((id) => ({ id, text: id }));

// Fits the filter side panel (w-80 = 320px minus p-4 padding on both sides).
const FILTER_WIDTH = 288;

// The viewer renders in a Shadow DOM, so global CSS can't reach it. Inject a
// style into the shadow root to hide the tab-bar "Open File" (+) button — we
// only ever show the generated report, never let the user open another file.
const hideOpenFileButton = (container: any) => {
  const root = container?.shadowRoot;
  if (!root || root.querySelector("style[data-hide-open-file]")) return;
  const style = document.createElement("style");
  style.setAttribute("data-hide-open-file", "");
  style.textContent = `button[aria-label="Open File"]{display:none!important;}`;
  root.appendChild(style);
};

// Label + info icon; the icon's `title` is shown by the Kendo Tooltip wrapper.
function FilterLabel({ text, info }: { text: string; info?: string }) {
  return (
    <label className="k-label k-margin-bottom-sm flex items-center gap-1">
      {text}
      {info && (
        <span title={info} style={{ display: "inline-flex" }}>
          <SvgIcon icon={infoCircleIcon} size="small" />
        </span>
      )}
    </label>
  );
}

// Build the StandardReportRequest, generate the report, and preview the PDF.
export default function ReportsPdfBuilder({
  machines,
  onGenerate,
  filtersOpen,
  onCloseFilters,
}: Props) {
  const localizationService = useLocalization();
  const siteName = useIdentityStore(
    (store) => store.user?.profile?.current_site_name
  );

  const [periodMode, setPeriodMode] = useState<PeriodMode>("months");
  const [display, setDisplay] = useState<string[]>(() => recentPeriods("months", 3));
  const [focus, setFocus] = useState<string[]>([]);
  const [machineIds, setMachineIds] = useState<string[]>([]);
  const [includeAnchors, setIncludeAnchors] = useState(false);

  const [loading, setLoading] = useState(true); // auto-generates on mount
  const [error, setError] = useState<string>("");
  const [hasReport, setHasReport] = useState(false);
  // Only user-triggered generations can be cancelled — the initial auto-load can't.
  const [canCancel, setCanCancel] = useState(false);

  const viewerRef = useRef<PDFViewerRef>(null);
  const machinesSeeded = useRef(false);
  const autoLoaded = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  const machineOptions = useMemo(
    () => machines.map((m) => ({ id: m.id, text: m.name })),
    [machines]
  );

  // Default to all machines selected, once the list loads (seed only once).
  useEffect(() => {
    if (!machinesSeeded.current && machines.length) {
      setMachineIds(machines.map((m) => m.id));
      machinesSeeded.current = true;
    }
  }, [machines]);

  const buildRequest = (): StandardReportRequest => {
    // Send focus periods in the same order as the display periods.
    const orderedFocus = display.filter((p) => focus.includes(p));
    return {
      machineIds,
      ...(periodMode === "months"
        ? { focusMonth: orderedFocus, displayMonths: display }
        : { focusWeek: orderedFocus, displayWeeks: display }),
      includeAnchors,
      selectedAnomalyIds: [],
    };
  };

  // Unique download name matching the backend convention:
  // "{SITE} - {YYYY-MM-DD HHmm}.pdf" (timestamped so each report is distinct).
  const reportFileName = () => {
    const stamp = moment().format("YYYY-MM-DD HHmm");
    const name = [siteName, stamp].filter(Boolean).join(" - ");
    // Strip characters not allowed in file names.
    return `${name.replace(/[/\\:*?"<>|]/g, " ").trim()}.pdf`;
  };

  const generate = async (cancellable = true) => {
    // Abort any in-flight request before starting a new one.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setCanCancel(cancellable);
    setError("");
    setLoading(true);
    try {
      const buffer = await onGenerate(buildRequest(), controller.signal); // PDF bytes

      const registry = await viewerRef.current?.registry;
      const docManager: any = (registry as any)
        ?.getPlugin?.("document-manager")
        ?.provides?.();
      docManager?.openDocumentBuffer({
        buffer,
        name: reportFileName(),
        autoActivate: true,
      });
      setHasReport(true);
    } catch (e: any) {
      // Swallow user-initiated cancellations — not a real error.
      if (controller.signal.aborted) return;
      setError(String(e?.message ?? e));
    } finally {
      // Only clear loading for the request that is still current — a stale
      // aborted request must not turn the spinner off on its successor.
      if (abortRef.current === controller) {
        abortRef.current = null;
        setLoading(false);
      }
    }
  };

  // Cancel the in-flight report request; leaves any existing report in place.
  const cancel = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setLoading(false);
  };

  // Auto-generate once on mount — defaults are already set, so show a report right away.
  useEffect(() => {
    if (autoLoaded.current) return;
    autoLoaded.current = true;
    generate(false); // initial auto-load — not cancellable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Abort any in-flight request when the component unmounts.
  useEffect(() => () => abortRef.current?.abort(), []);

  const switchMode = (mode: PeriodMode) => {
    if (mode === periodMode) return; // no-op when already in this mode
    setPeriodMode(mode);
    setDisplay(recentPeriods(mode, 3)); // seed defaults synchronously (no flicker)
    setFocus([]);
  };

  return (
    <div className="flex h-full gap-4">
      {/* PDF viewer — left, fills the remaining space */}
      <div className="relative min-w-0 flex-1 rounded border border-gray-200">
        {loading && (
          <div
            className={`absolute inset-0 z-10 flex items-center justify-center ${
              hasReport ? "bg-white/70" : "bg-white"
            }`}
          >
            <LoadingSpinner className="h-full" />
          </div>
        )}
        {!hasReport && !loading && (
          <div className="gb-text-normal absolute inset-0 z-10 flex flex-col items-center justify-center gap-5 bg-gray-50 px-6 text-center text-lg">
            <span className="inline-flex h-[70px] w-[70px] items-center justify-center">
              <SvgIcon icon={graphIcon} size="xxxlarge" />
            </span>
            <span>
              {localizationService.toLanguageString(
                "custom.setFiltersText",
                enMessages.custom.setFiltersText
              )}
            </span>
          </div>
        )}
        <PDFViewer
          ref={viewerRef}
          onInit={hideOpenFileButton}
          config={{
            zoom: { defaultZoomLevel: ZoomMode.FitWidth },
            annotations: { locked: { type: LockModeType.All } },
            theme: {
              preference: "light",
              light: {
                accent: { primary: "#cb2429", primaryHover: "#b91d22" },
              },
            },
            // Keep the report tabs, but drop Open/Close/Security from the menu.
            // (The tab-bar "+" button is hidden separately via hideOpenFileButton.)
            disabledCategories: [
              "insert",
              "form",
              "redaction",
              "document-open",
              "document-close",
              "document-protect",
            ],
          }}
          style={{ height: "100%", width: "100%" }}
        />
      </div>

      {/* Filters — side panel to the right (pushes the report, does not overlay) */}
      {filtersOpen && (
        <div className="flex w-80 shrink-0 flex-col rounded border border-gray-200">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2">
            <span className="font-bold">Filters</span>
            <Button fillMode="flat" svgIcon={xIcon} onClick={onCloseFilters} />
          </div>

          <div className="min-h-0 flex-1 overflow-auto p-4">
            <Tooltip
              anchorElement="target"
              position="top"
              openDelay={100}
              parentTitle={true}
            >
              <div className="flex flex-col gap-4">
                {/* Period mode */}
                <div>
                  <FilterLabel text="Period" />
                  <ButtonGroup>
                    <Button
                      themeColor={periodMode === "months" ? "primary" : "base"}
                      onClick={() => switchMode("months")}
                      disabled={loading}
                    >
                      Months
                    </Button>
                    <Button
                      themeColor={periodMode === "weeks" ? "primary" : "base"}
                      onClick={() => switchMode("weeks")}
                      disabled={loading}
                    >
                      Weeks
                    </Button>
                  </ButtonGroup>
                </div>

                {/* Display periods (columns) — grouped by year */}
                <div>
                  <FilterLabel
                    text={`Display ${periodMode}`}
                    info={
                      periodMode === "months"
                        ? "Periods shown in the report — maximum of 6 months."
                        : "Periods shown in the report — maximum of 6 weeks."
                    }
                  />
                  <PeriodYearTree
                    mode={periodMode}
                    value={display}
                    onChange={(vals) => {
                      setDisplay(vals);
                      setFocus((f) => f.filter((p) => vals.includes(p)));
                    }}
                    width={FILTER_WIDTH}
                    disabled={loading}
                  />
                </div>

                {/* Focus periods */}
                <div>
                  <FilterLabel
                    text={`Focus ${periodMode}`}
                    info={
                      periodMode === "months"
                        ? "Includes detailed data for the chosen months."
                        : "Includes detailed data for the chosen weeks."
                    }
                  />
                  <SelectAllMultiSelectTree
                    options={toOptions(display)}
                    value={focus}
                    onChange={setFocus}
                    tagLabel="focus"
                    selectAllLabel="Select all"
                    groupKey="focus"
                    width={FILTER_WIDTH}
                    matchPopupWidth
                    showTags
                    disabled={loading}
                  />
                </div>

                {/* Machines */}
                <div>
                  <FilterLabel text="Machines" />
                  <SelectAllMultiSelectTree
                    options={machineOptions}
                    value={machineIds}
                    onChange={setMachineIds}
                    tagLabel="machines"
                    selectAllLabel="All machines"
                    groupKey="machines"
                    filterable
                    width={FILTER_WIDTH}
                    matchPopupWidth
                    disabled={loading}
                  />
                </div>

                {/* Include anchors */}
                <div>
                  <FilterLabel text="Anchors" />
                  <div className="flex h-9 items-center gap-2">
                    <Switch
                      checked={includeAnchors}
                      onChange={(e) => setIncludeAnchors(e.value)}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            </Tooltip>

            {error && (
              <div className="mt-3 text-sm text-primary">Error: {error}</div>
            )}
          </div>

          <div className="border-t border-gray-200 p-3">
            {loading && canCancel ? (
              <Button
                svgIcon={xIcon}
                themeColor="base"
                onClick={cancel}
                className="w-full"
              >
                Cancel Generation
              </Button>
            ) : (
              <Button
                svgIcon={graphIcon}
                themeColor="primary"
                onClick={() => generate()}
                disabled={loading || display.length === 0}
                className="w-full"
              >
                {loading
                  ? "Generating…"
                  : localizationService.toLanguageString(
                      "custom.generateReport",
                      enMessages.custom.generateReport
                    )}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
